import { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useKeyboardHandler } from 'react-native-keyboard-controller';
import { runOnJS } from 'react-native-reanimated';
import { calculateSize, TOOLBAR_PADDING_VERTICAL } from '../config';
const isOSKeyboard = (keyboardType) => keyboardType === 'KeyboardAmountUpper' ||
    keyboardType === 'KeyboardAmountLower' ||
    keyboardType === undefined ||
    keyboardType === 'KeyboardReceipts';
const useKeyboard = (activeFilter, onSelectFilter, onRemoveFilter, onToggleAmountFilter) => {
    /**
     * Space reserved for the current custom keyboard.
     * It’s measured whenever the OS keyboard pops up
     */
    const [specialKeyboardHeight, setSpecialKeyboardHeight] = useState(0);
    const latestKeyboardHeight = useRef(0);
    /**
     * Name of the current custom keyboard.
     * Undefined means there’s the default text keyboard, or the keyboard is hidden
     */
    const [activeKeyboard, setActiveKeyboard] = useState();
    const [nativeKeyboardHeight, setNativeKeyboardHeight] = useState(0);
    // Use refs to avoid recomputing callbacks on state changes
    const activeKeyboardRef = useRef(undefined);
    const activeFilterRef = useRef(undefined);
    const keyboardHeightRef = useRef(0);
    /**
     *  Some custom keyboards require invoking the OS keyboard
     *  In that case OS keyboard show/hide events should be handled differently
     *  This flag is used to distinguish between custom -> OS / custom -> custom
     */
    const waitingForOSKeyboardTransitionRef = useRef(false);
    // Sync refs with the state
    useEffect(() => {
        activeKeyboardRef.current = activeKeyboard;
    }, [activeKeyboard]);
    useEffect(() => {
        activeFilterRef.current = activeFilter;
    }, [activeFilter]);
    useEffect(() => {
        keyboardHeightRef.current = specialKeyboardHeight;
    }, [specialKeyboardHeight]);
    const handleActiveKeyboard = useCallback((activeKeyboard) => {
        if (activeKeyboard) {
            if (activeKeyboard === 'KeyboardAmountLower') {
                onToggleAmountFilter('AMOUNT_LOWER');
            }
            else if (activeKeyboard === 'KeyboardAmountUpper') {
                onToggleAmountFilter('AMOUNT_UPPER');
            }
            else if (activeKeyboard === 'KeyboardReceipts') {
                onSelectFilter({
                    badgeText: 'Has receipt',
                    filterType: 'RECEIPT',
                });
            }
            else {
                onToggleAmountFilter();
                Keyboard.dismiss();
            }
        }
    }, [onSelectFilter, onToggleAmountFilter]);
    const showSpecialKeyboard = useCallback((keyboard) => {
        waitingForOSKeyboardTransitionRef.current = isOSKeyboard(activeKeyboardRef.current) || isOSKeyboard(keyboard);
        if (keyboard === 'KeyboardReceipts') {
            if (activeFilterRef.current?.RECEIPT) {
                onRemoveFilter(activeFilterRef.current.RECEIPT);
            }
            else {
                handleActiveKeyboard(keyboard);
            }
            return;
        }
        if (keyboard && keyboard === activeKeyboardRef.current) {
            return;
        }
        if (keyboard === undefined && nativeKeyboardHeight === 0) {
            setSpecialKeyboardHeight(0);
        }
        else {
            setSpecialKeyboardHeight(latestKeyboardHeight.current);
        }
        setActiveKeyboard(keyboard);
        handleActiveKeyboard(keyboard);
    }, [handleActiveKeyboard, nativeKeyboardHeight, onRemoveFilter]);
    const collapseSpecialKeyboard = useCallback(() => {
        showSpecialKeyboard(undefined);
        setSpecialKeyboardHeight(0);
    }, [showSpecialKeyboard]);
    const hideSpecialKeyboard = useCallback(() => {
        collapseSpecialKeyboard();
        Keyboard.dismiss();
    }, [collapseSpecialKeyboard]);
    // Used by SearchTransactions.js when we scroll transaction list
    const keyboardHideFromScroll = useCallback(() => {
        if (keyboardHeightRef.current) {
            collapseSpecialKeyboard();
        }
        if (nativeKeyboardHeight) {
            Keyboard.dismiss();
        }
    }, [collapseSpecialKeyboard, nativeKeyboardHeight]);
    const { width } = useAppFrameDimensions();
    const handleKeyboardShow = useCallback((height) => {
        setNativeKeyboardHeight(height);
        waitingForOSKeyboardTransitionRef.current = false;
        const size = calculateSize(width);
        const keyboardCombinedHeight = height + size + TOOLBAR_PADDING_VERTICAL * 2;
        setSpecialKeyboardHeight(keyboardCombinedHeight);
        latestKeyboardHeight.current = keyboardCombinedHeight;
    }, [width]);
    const handleKeyboardHide = useCallback(() => {
        if (waitingForOSKeyboardTransitionRef.current && activeKeyboard) {
            waitingForOSKeyboardTransitionRef.current = false;
            return;
        }
        setNativeKeyboardHeight(0);
        onToggleAmountFilter();
        collapseSpecialKeyboard();
    }, [activeKeyboard, onToggleAmountFilter, collapseSpecialKeyboard]);
    useKeyboardHandler({
        onStart: (e) => {
            'worklet';
            if (e.progress === 1) {
                runOnJS(handleKeyboardShow)(e.height);
            }
            if (e.progress === 0) {
                runOnJS(handleKeyboardHide)();
            }
        },
    }, [handleKeyboardShow, handleKeyboardHide]);
    return {
        keyboardHeight: specialKeyboardHeight,
        activeKeyboard,
        showSpecialKeyboard,
        hideSpecialKeyboard,
        keyboardHideFromScroll,
    };
};
export default useKeyboard;
